import { FC, useEffect, useState } from "react";
import styles from "./contract-works.module.scss";
import { useParams } from "react-router-dom";
import { PreloaderBar } from "../../../../ui/preloaderBar/PreloaderBar";
import { Snackbar } from "../../../../ui/snackbar/Snackbar";
import { TableRowActions } from "../../../../ui/tableRowActions/tableRowActions";
import {
  useDeleteContractWorkMutation,
  useGetAllContractWorksQuery,
} from "./contractWorksApi";
import { Confirm } from "../../../../ui/confirm/confirm";
import { Button } from "../../../../ui/button/Button";
import { IContractWorkInfo } from "./IContractWork";
import { AddContractWork } from "./addContractWork/addContractWork";
import { EditContractWork } from "./editContractWork/editContractWork";
import { min2HHMM } from "../../../../helpers/time";

type RowProps = IContractWorkInfo & {
  handler: (type: "edit" | "delete", id: number) => boolean;
};

const Row: FC<RowProps> = ({
  id,
  work,
  aircraft_type,
  limit_time,
  cost,
  handler,
}) => {
  return (
    <tr>
      <td>{aircraft_type}</td>
      <td>{work}</td>
      <td>{min2HHMM(limit_time)}</td>
      <td>{cost / 100}</td>
      <td>
        <TableRowActions id={id} handler={handler} />
      </td>
    </tr>
  );
};

export const ContractWorks: FC = () => {
  const { id } = useParams();

  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isUXOpen, setIsUXOpen] = useState(true);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [ID, setID] = useState<number | null>(null);

  const { data, error, isError, isFetching } = useGetAllContractWorksQuery(
    Number(id)
  );
  const [
    deleteWork,
    {
      isError: isDeleteError,
      isLoading: isDeleteProcessing,
      isSuccess: isDeleteSuccess,
    },
  ] = useDeleteContractWorkMutation();

  const create = () => {
    setIsAddOpen(true);
  };

  const rowHandler = (type: "edit" | "delete", id: number) => {
    if (isConfirmOpen) return false;
    setIsUXOpen(true);
    setID(id);
    switch (type) {
      case "delete":
        setIsConfirmOpen(true);
        break;

      case "edit":
        setIsEditOpen(true);
        break;

      default:
        break;
    }
    return true;
  };

  useEffect(() => {
    if (!isDeleteSuccess) return;

    setIsConfirmOpen(false);
    setID(null);
  }, [isDeleteSuccess]);

  return (
    <section className={styles.main}>
      <header className={styles.header}>
        <h2>Стоимость 1 часа по видам работ</h2>
        <div className={styles.actions}>
          <Button onClick={create}>Добавить</Button>
        </div>
      </header>
      <table className={styles.table}>
        <thead>
          <tr>
            <th>Тип ВС</th>
            <th>Вид работ</th>
            <th>Лимит летного времени, час:мин</th>
            <th>Стоимость 1 часа, руб.</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {data &&
            data.map((row) => (
              <Row
                key={row.id}
                id={row.id}
                work={row.work}
                aircraft_type={row.aircraft_type}
                limit_time={row.limit_time}
                cost={row.cost}
                handler={rowHandler}
              />
            ))}
        </tbody>
      </table>
      {isFetching && <PreloaderBar />}
      {isAddOpen && id && (
        <AddContractWork
          close={() => setIsAddOpen(false)}
          contract_id={Number(id)}
        />
      )}
      {isEditOpen && ID && Number(ID) && Number(id) && (
        <EditContractWork
          close={() => setIsEditOpen(false)}
          id={ID}
          contract_id={Number(id)}
        />
      )}
      {isError && "status" in error && error.status !== 404 && isUXOpen && (
        <Snackbar
          className="error"
          isOpen={isError}
          onClose={() => setIsUXOpen(false)}
        >
          Ошибка загрузки данных. Попробуйте позже
        </Snackbar>
      )}
      {isConfirmOpen && ID && (
        <Confirm
          confirm={() => deleteWork(ID)}
          reject={() => {
            setIsConfirmOpen(false);
            setID(null);
          }}
          text="Вы действительно хотите удалить вид работ из системы?"
          isProcessing={isDeleteProcessing}
        />
      )}
      {isDeleteSuccess && isUXOpen && (
        <Snackbar
          className="success"
          isOpen={isDeleteSuccess}
          onClose={() => setIsUXOpen(false)}
        >
          Вид работ удален
        </Snackbar>
      )}
      {isDeleteError && isUXOpen && (
        <Snackbar
          className="error"
          isOpen={isDeleteError}
          onClose={() => setIsUXOpen(false)}
        >
          Ошибка удаления. Попробуйте позже
        </Snackbar>
      )}
    </section>
  );
};
